import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import useOptions from "../../../hooks/useOptions";
import { useSnackbar } from "../../../components/snackbar/SnackbarProvider";
import {
  createAdminFollowUpType,
  fetchAdminFollowUpTypes,
  fetchAdminSampleTypes,
  updateAdminFollowUpType,
} from "../../../utils/api";

const maroon = "#500000";

const EMPTY_DRAFT = {
  followUpType: "",
  duration: "",
  durationUnit: "",
  gap: "",
  gapUnit: "",
  sampleTypes: [],
};

const FollowUpTypeManagementCard = () => {
  const [rows, setRows] = useState([]);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const { openSnackbar } = useSnackbar();

  const {
    options: durationUnitOptions,
    loading: durationUnitsLoading,
  } = useOptions("enums/DurationUnit");

  const sampleTypeMap = useMemo(() => {
    const map = new Map();
    sampleTypes.forEach((sampleType) => {
      map.set(sampleType.sampleLetter, sampleType);
    });
    return map;
  }, [sampleTypes]);

  const renderSampleTypeTooltip = (code) => {
    const sampleType = sampleTypeMap.get(code);
    if (!sampleType) {
      return code;
    }
    return `${sampleType.sampleLetter} - ${sampleType.shortName || sampleType.longName || sampleType.sampleLetter}`;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setPageError("");
      const [followUpResponse, sampleTypeResponse] = await Promise.all([
        fetchAdminFollowUpTypes(),
        fetchAdminSampleTypes(),
      ]);
      setRows(Array.isArray(followUpResponse) ? followUpResponse : []);
      setSampleTypes(Array.isArray(sampleTypeResponse) ? sampleTypeResponse : []);
    } catch (error) {
      console.error("Error loading follow-up types:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to load follow-up types.";
      setPageError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.followUpType);
    setDraft({
      followUpType: row.followUpType || "",
      duration: row.duration ?? "",
      durationUnit: row.durationUnit || "",
      gap: row.gap ?? "",
      gapUnit: row.gapUnit || "",
      sampleTypes: row.sampleTypes || [],
    });
    setFormError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!draft.followUpType.trim()) {
      setFormError("Follow-up type is required.");
      return;
    }
    if (draft.sampleTypes.length === 0) {
      setFormError("Select at least one sample type.");
      return;
    }
    if (!draft.duration || !draft.durationUnit || !draft.gap || !draft.gapUnit) {
      setFormError("Duration, duration unit, gap, and gap unit are required.");
      return;
    }
    if (Number(draft.duration) <= 0 || Number(draft.gap) <= 0) {
      setFormError("Duration and gap must be positive numbers greater than 0.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        followUpType: draft.followUpType.trim(),
        duration: Number(draft.duration),
        durationUnit: draft.durationUnit,
        gap: Number(draft.gap),
        gapUnit: draft.gapUnit,
        sampleTypes: draft.sampleTypes,
      };

      if (editingId) {
        await updateAdminFollowUpType(editingId, payload);
        openSnackbar("Follow-up type updated successfully!", "success");
      } else {
        await createAdminFollowUpType(payload);
        openSnackbar("Follow-up type created successfully!", "success");
      }

      setDialogOpen(false);
      setDraft(EMPTY_DRAFT);
      await loadData();
    } catch (error) {
      console.error("Error saving follow-up type:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to save follow-up type.";
      setFormError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card elevation={1} sx={{ boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)", borderRadius: 0 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: maroon }}>
              Follow-Up Type Management
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                color: "#666",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 0.5,
                px: 1.5,
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)", color: "#333" },
              }}
            >
              ADD FOLLOW-UP TYPE
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {pageError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {pageError}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
            gap: 3,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 2,
              minHeight: 84,
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: maroon, fontWeight: 700, fontSize: "0.8rem" }}
            >
              TOTAL FOLLOW-UP TYPES
            </Typography>
            <Typography variant="h3" sx={{ color: maroon, fontWeight: 700 }}>
              {rows.length}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0, pr: { xs: 0, md: 4 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "120px minmax(0, 0.5fr) minmax(0, 0.5fr) minmax(0, 1fr) 96px",
                gap: 2,
                pb: 1,
              }}
            >
              {["TYPE", "DURATION", "GAP", "SAMPLE TYPES", "ACTION"].map((header, index) => (
                <Typography
                  key={header}
                  variant="caption"
                  sx={{
                    color: "#666",
                    fontWeight: 700,
                    textAlign: index === 4 ? "right" : "left",
                  }}
                >
                  {header}
                </Typography>
              ))}
            </Box>

            {loading ? (
              <Box sx={{ py: 5, textAlign: "center", color: "#666" }}>
                Loading follow-up types...
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center", color: "#666" }}>
                No follow-up types found.
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {rows.map((row, index) => (
                  <ListItem
                    key={row.followUpType}
                    sx={{
                      px: 0,
                      py: 0.75,
                      display: "grid",
                      gridTemplateColumns: "120px minmax(0, 0.5fr) minmax(0, 0.5fr) minmax(0, 1fr) 96px",
                      gap: 2,
                      borderTop: index === 0 ? "1px solid #eee" : 0,
                      borderBottom: index === rows.length - 1 ? 0 : "1px solid #eee",
                      alignItems: "center",
                    }}
                  >
                    <ListItemText
                      primary={row.followUpType}
                      primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
                      sx={{ m: 0 }}
                    />
                    <Typography variant="body2">{`${row.duration} ${row.durationUnit}`}</Typography>
                    <Typography variant="body2">{`${row.gap} ${row.gapUnit}`}</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                      {(row.sampleTypes || []).map((sampleType) => (
                        <Tooltip
                          key={`${row.followUpType}-${sampleType}`}
                          title={renderSampleTypeTooltip(sampleType)}
                        >
                          <Chip size="small" label={sampleType} />
                        </Tooltip>
                      ))}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: maroon }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: maroon, color: "white" }}>
          {editingId ? "Edit Follow-Up Type" : "Create New Follow-Up Type"}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <TextField
              label="Follow-Up Type"
              value={draft.followUpType}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, followUpType: event.target.value }))
              }
              disabled={saving || !!editingId}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Sample Types</InputLabel>
              <Select
                multiple
                value={draft.sampleTypes}
                label="Sample Types"
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, sampleTypes: event.target.value }))
                }
                renderValue={(selected) =>
                  selected.map((value) => renderSampleTypeTooltip(value)).join(", ")
                }
              >
                {sampleTypes.map((sampleType) => (
                  <MenuItem key={sampleType.sampleLetter} value={sampleType.sampleLetter}>
                    <ListItemText primary={renderSampleTypeTooltip(sampleType.sampleLetter)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Duration"
              type="number"
              value={draft.duration}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, duration: event.target.value }))
              }
              inputProps={{ min: 1 }}
              fullWidth
            />

            <FormControl fullWidth disabled={durationUnitsLoading}>
              <InputLabel>Duration Unit</InputLabel>
              <Select
                value={draft.durationUnit}
                label="Duration Unit"
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, durationUnit: event.target.value }))
                }
              >
                {durationUnitOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Gap"
              type="number"
              value={draft.gap}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, gap: event.target.value }))
              }
              inputProps={{ min: 1 }}
              fullWidth
            />

            <FormControl fullWidth disabled={durationUnitsLoading}>
              <InputLabel>Gap Unit</InputLabel>
              <Select
                value={draft.gapUnit}
                label="Gap Unit"
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, gapUnit: event.target.value }))
                }
              >
                {durationUnitOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setDialogOpen(false); setFormError(""); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || durationUnitsLoading}
            sx={{ backgroundColor: maroon, "&:hover": { backgroundColor: "#4a0000" } }}
          >
            {saving ? "Saving..." : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FollowUpTypeManagementCard;
